import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  formatRgbLabel,
  normalizeGroupementColor,
  pickUnusedGroupementColor,
  sanitizeGroupementTitleInput,
  validateGroupementTitle,
  capitalizeGroupementTitleFirstLetter,
} from "../utils/groupementTitle";
import {
  getGroupingsScrollStorageKey,
  readGroupingsScrollMap,
  writeGroupingsScrollMap,
} from "../utils/groupingsListPreference";
import "./GroupingsView.css";
import "./WordListBox.css";
import "./RegisterSkeleton.css";

const GROUPINGS_LIST_SKELETON_NAME_WIDTHS = [
  92, 148, 78, 126, 110, 168, 86, 134, 98, 154, 72, 118, 140, 88, 176, 80, 128,
  104, 160, 76, 120, 136,
];

function GroupingsListSkeleton() {
  return (
    <div
      className="word-listbox-shell word-listbox-shell--skeleton"
      aria-hidden="true"
      aria-busy="true"
    >
      <div className="listbox custom-scroll">
        {GROUPINGS_LIST_SKELETON_NAME_WIDTHS.map((width, index) => (
          <div key={index} className="word-list-item">
            <span className="groupement-option">
              <span className="groupement-option__swatch register-skel" />
              <span className="groupement-option__name">
                <span
                  className="register-skel register-skel--label"
                  style={{ width: `${width}px` }}
                />
              </span>
              <span className="groupement-option__count register-skel" />
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function GroupementColorLabel({ color, title, count = null, empty = false }) {
  return (
    <span className="groupement-option">
      {empty ? (
        <span className="groupement-option__swatch groupement-option__swatch--empty" />
      ) : (
        <span
          className="groupement-option__swatch"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
      )}
      <span className="groupement-option__name">{title}</span>
      {count != null ? (
        <span
          className="groupement-option__count"
          aria-label={`${Number(count).toLocaleString("fr-FR")} mot${
            Number(count) > 1 ? "s" : ""
          }`}
        >
          {Number(count).toLocaleString("fr-FR")}
        </span>
      ) : null}
    </span>
  );
}

export function GroupingsListPanel({
  groupings = [],
  selectedGroupingId = null,
  onSelectGrouping = () => {},
  keyboardNavEnabled = true,
  loading = false,
  scrollStorageKey = getGroupingsScrollStorageKey(),
}) {
  const listRef = useRef(null);
  const itemRefs = useRef({});
  const scrollPositionsRef = useRef(null);
  if (scrollPositionsRef.current === null) {
    scrollPositionsRef.current = readGroupingsScrollMap();
  }
  const scrollStorageKeyRef = useRef(scrollStorageKey);
  const pendingRestoreRef = useRef(true);
  const persistTimerRef = useRef(null);
  const groupingsRef = useRef(groupings);
  const selectedGroupingIdRef = useRef(selectedGroupingId);
  const scrollIntoViewOnSelectRef = useRef(false);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  groupingsRef.current = groupings;
  selectedGroupingIdRef.current = selectedGroupingId;

  if (scrollStorageKeyRef.current !== scrollStorageKey) {
    scrollStorageKeyRef.current = scrollStorageKey;
    pendingRestoreRef.current = true;
  }

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) {
      setScrollFades({ top: false, bottom: false });
      return;
    }
    const threshold = 6;
    setScrollFades({
      top: el.scrollTop > threshold,
      bottom: el.scrollTop + el.clientHeight < el.scrollHeight - threshold,
    });
  }, []);

  const flushScroll = useCallback(() => {
    const el = listRef.current;
    if (el && el.clientHeight > 0) {
      scrollPositionsRef.current[scrollStorageKeyRef.current] = el.scrollTop;
    }
    writeGroupingsScrollMap(scrollPositionsRef.current);
  }, []);

  const tryRestoreScroll = useCallback(() => {
    if (!pendingRestoreRef.current) return;
    const el = listRef.current;
    if (!el || el.clientHeight === 0) return;

    const selectedId = selectedGroupingIdRef.current;
    const exists = groupingsRef.current.some(
      (grouping) => grouping.groupementId === selectedId
    );
    if (exists && !itemRefs.current[selectedId]) return;

    const saved = scrollPositionsRef.current[scrollStorageKeyRef.current];
    if (typeof saved === "number" && saved > 0) {
      el.scrollTop = saved;
    }

    const item = exists ? itemRefs.current[selectedId] : null;
    if (item) {
      const elRect = el.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();
      const fullyVisible =
        itemRect.top >= elRect.top && itemRect.bottom <= elRect.bottom;
      if (!fullyVisible) {
        const delta =
          itemRect.top - elRect.top - el.clientHeight / 2 + itemRect.height / 2;
        el.scrollTop += delta;
      }
    }

    pendingRestoreRef.current = false;
    updateScrollFades();
  }, [updateScrollFades]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (el) {
      scrollPositionsRef.current[scrollStorageKey] = el.scrollTop;
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
      persistTimerRef.current = setTimeout(() => {
        writeGroupingsScrollMap(scrollPositionsRef.current);
      }, 120);
    }
    updateScrollFades();
  }, [scrollStorageKey, updateScrollFades]);

  useLayoutEffect(() => {
    pendingRestoreRef.current = true;
    updateScrollFades();
    let frame2;
    const frame1 = requestAnimationFrame(() => {
      tryRestoreScroll();
      frame2 = requestAnimationFrame(tryRestoreScroll);
    });
    return () => {
      cancelAnimationFrame(frame1);
      if (frame2) cancelAnimationFrame(frame2);
    };
  }, [groupings, scrollStorageKey, tryRestoreScroll, updateScrollFades]);

  useEffect(() => {
    const onBeforeUnload = () => flushScroll();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      flushScroll();
      if (persistTimerRef.current) clearTimeout(persistTimerRef.current);
    };
  }, [flushScroll]);

  useEffect(() => {
    const el = listRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(() => {
      updateScrollFades();
      tryRestoreScroll();
    });
    observer.observe(el);
    window.addEventListener("resize", updateScrollFades);
    window.visualViewport?.addEventListener("resize", updateScrollFades);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateScrollFades);
      window.visualViewport?.removeEventListener("resize", updateScrollFades);
    };
  }, [tryRestoreScroll, updateScrollFades]);

  const navigateGrouping = useCallback(
    (direction) => {
      if (!groupings.length) return;
      const currentIndex = groupings.findIndex(
        (grouping) => grouping.groupementId === selectedGroupingId
      );
      let nextIndex;
      if (currentIndex < 0) {
        nextIndex = direction === "down" ? 0 : groupings.length - 1;
      } else {
        nextIndex =
          direction === "down"
            ? (currentIndex + 1) % groupings.length
            : (currentIndex - 1 + groupings.length) % groupings.length;
      }
      const next = groupings[nextIndex];
      if (!next || next.groupementId === selectedGroupingId) return;
      scrollIntoViewOnSelectRef.current = true;
      onSelectGrouping(next.groupementId);
    },
    [groupings, onSelectGrouping, selectedGroupingId]
  );

  useEffect(() => {
    if (!scrollIntoViewOnSelectRef.current) return;
    scrollIntoViewOnSelectRef.current = false;
    if (!selectedGroupingId) return;
    itemRefs.current[selectedGroupingId]?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }, [selectedGroupingId]);

  useEffect(() => {
    if (!keyboardNavEnabled) return undefined;

    const handleGlobalKeyDown = (event) => {
      if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
      const tag = event.target?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea" || event.target?.isContentEditable) {
        return;
      }
      if (!groupings.length) return;
      event.preventDefault();
      navigateGrouping(event.key === "ArrowDown" ? "down" : "up");
    };

    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, [groupings.length, keyboardNavEnabled, navigateGrouping]);

  if (loading) {
    return <GroupingsListSkeleton />;
  }

  return (
    <div
      className={[
        "word-listbox-shell",
        scrollFades.top && "word-listbox-shell--fade-top",
        scrollFades.bottom && "word-listbox-shell--fade-bottom",
      ]
        .filter(Boolean)
        .join(" ")}
      aria-label="Liste des groupements thématiques"
    >
      <div
        ref={listRef}
        className="listbox custom-scroll"
        onScroll={handleScroll}
      >
        {groupings.length === 0 ? (
          <div className="groupings-list-empty">
            Aucun groupement thématique pour le moment
          </div>
        ) : (
          groupings.map((grouping) => (
            <div
              key={grouping.groupementId}
              ref={(node) => {
                if (node) itemRefs.current[grouping.groupementId] = node;
                else delete itemRefs.current[grouping.groupementId];
              }}
              className={`word-list-item${
                grouping.groupementId === selectedGroupingId
                  ? " word-list-item--selected"
                  : ""
              }`}
              onClick={() => onSelectGrouping(grouping.groupementId)}
            >
              <GroupementColorLabel
                color={grouping.color}
                title={grouping.title}
                count={grouping.wordCount ?? 0}
              />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export function GroupingCreatePanel({
  usedColors = [],
  onCancel = () => {},
  onCreated = () => {},
}) {
  const inputRef = useRef(null);
  const [title, setTitle] = useState("");
  const [color, setColor] = useState(() => pickUnusedGroupementColor(usedColors));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  const titleError = validateGroupementTitle(title);
  const normalizedColor = normalizeGroupementColor(color);
  const usedNormalizedColors = useMemo(
    () => usedColors.map((value) => normalizeGroupementColor(value)).filter(Boolean),
    [usedColors]
  );
  const colorTaken = usedNormalizedColors.includes(normalizedColor);
  const canSubmit = !titleError && Boolean(normalizedColor) && !colorTaken && !saving;

  const handleTitleChange = (event) => {
    setTitle(
      capitalizeGroupementTitleFirstLetter(
        sanitizeGroupementTitleInput(event.target.value)
      )
    );
    setError("");
  };

  const handleColorChange = (event) => {
    setColor(event.target.value);
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError("");
    try {
      await onCreated({
        title: capitalizeGroupementTitleFirstLetter(title),
        color: normalizedColor,
      });
    } catch (err) {
      setError(err.message || "Impossible de créer le groupement thématique.");
      setSaving(false);
    }
  };

  return (
    <div className="containerDetailMot-shell">
      <div className="containerDetailMot detail-scroll">
        <form className="groupement-create" onSubmit={handleSubmit}>
          <header className="groupement-create__header">
            <h2 className="groupement-create__title">
              Ajout d&apos;un nouveau groupement thématique
            </h2>
            <p className="groupement-create__intro">
              Un groupement thématique réunit des mots autour d&apos;une même idée :
              qualifier une personne, décrire une apparence, parler d&apos;architecture.
              Donnez-lui un nom distinct et une couleur unique pour le retrouver
              facilement dans le registre.
            </p>
          </header>

          <label className="groupement-create__question" htmlFor="groupement-title-input">
            Sous quel nom souhaitez-vous définir ce nouveau regroupement thématique ?
          </label>
          <input
            id="groupement-title-input"
            ref={inputRef}
            className="groupement-create__input"
            type="text"
            value={title}
            onChange={handleTitleChange}
            maxLength={40}
            autoComplete="off"
            spellCheck="false"
            aria-invalid={Boolean(title) && Boolean(titleError)}
          />
          {title && titleError ? (
            <p className="groupement-create__hint">{titleError}</p>
          ) : (
            <p className="groupement-create__hint">
              5 lettres minimum, 40 caractères maximum. Lettres, accents,
              virgule et parenthèses.
            </p>
          )}

          <div className="groupement-create__color">
            <span className="groupement-create__color-label">Couleur du groupement</span>
            <div className="groupement-create__color-row">
              <input
                type="color"
                className="groupement-create__color-picker"
                value={normalizedColor || "#6b4226"}
                onChange={handleColorChange}
                aria-label="Choisir la couleur du groupement thématique"
              />
              <span
                className="groupement-create__swatch"
                style={{ backgroundColor: normalizedColor || "#6b4226" }}
                aria-hidden="true"
              />
              <span className="groupement-create__rgb">
                {formatRgbLabel(normalizedColor)}
              </span>
            </div>
            {colorTaken ? (
              <p className="groupement-create__hint">
                Cette couleur est déjà utilisée par un autre groupement.
              </p>
            ) : null}
          </div>

          {error ? <p className="groupement-create__error">{error}</p> : null}

          <div className="groupement-create__actions">
            <button
              type="button"
              className="word-edit-btn word-edit-btn--cancel"
              onClick={onCancel}
              disabled={saving}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="word-edit-btn word-edit-btn--primary"
              disabled={!canSubmit}
            >
              {saving ? "Validation…" : "Valider"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export function GroupingSuccessPanel({ title }) {
  return (
    <div className="containerDetailMot-shell">
      <div className="containerDetailMot detail-scroll">
        <p className="groupings-detail-empty groupings-detail-empty--success">
          Le groupement thématique {title ? `« ${title} »` : ""} a été créé ! Pour
          ajouter un mot dans ce groupement, choisissez un mot dans le registre et
          assignez-le à ce groupement dans la fiche du mot (définition et modifications).
        </p>
      </div>
    </div>
  );
}
